// components/ReclutamientoForm.jsx - Con Regiones de Ventas relacionadas
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../AuthContext';
import { useQuery } from '../QueryContext';
import SearchableSelect from './SearchableSelect';
import { 
  X, 
  UserCheck, 
  UserPlus, 
  Save, 
  Loader2,
  BriefcaseBusiness,
  BadgeCheck,
  FileCheck2
} from 'lucide-react';

const solicitudInicial = {
  tipo_solicitud: 'Existente',
  cargo: '',
  cargo_id: null,
  departamento: '',
  ubicacion: '',
  region: '',
  region_ventas: '',
  status: 'Vacante',
  motivo: 'Sustitución',
  tipo_vacante: 'A',
  solicitante_id: null,
  solicitante_nombre: '',
  solicitante_cargo: '',
  solicitante_rol: '',
  solicitante_area: '',
  solicitante_subarea1: '',
  solicitante_subarea2: '',
};

// Mapa de Región → Región de Ventas
const regionVentasMap = {
  'Los Andes': 'ANDES (TACHIRA MERIDA TRUJILLO)',
  'Capital': 'CAPITAL (MIRANDA VARGAS)',
  'Centro': 'CENTRO (GUARICO ARAGUA CARABOBO COJEDES)',
  'Centroccidente': 'CENTRO OCCIDENTE (LARA BARINAS APURE PORTUGUESA YARACUY)',
  'Oriente': 'ORIENTE NORTE (SUCRE ANZOATEGUI)',
  'Occidente': 'OCCIDENTE (FALCON ZULIA)',
};

export default function ReclutamientoForm({ 
  isOpen, 
  onClose, 
  onSubmit, 
  editData = null,
  isEditing = false 
}) {
  const { profile } = useAuth();
  const { getCargosDisponibles, getLiderDirecto } = useQuery();
  
  const [formData, setFormData] = useState(solicitudInicial);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [cargosFiltrados, setCargosFiltrados] = useState([]);
  const [liderDirecto, setLiderDirecto] = useState(null);
  const [autoAprobacion, setAutoAprobacion] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  // Opciones para los selects
  const ubicacionesOptions = [
    { value: 'Torre', label: 'Torre' },
    { value: 'Almacén', label: 'Almacén' },
    { value: 'Zulia', label: 'Zulia' },
    { value: 'Valencia', label: 'Valencia' },
    { value: 'Caracas', label: 'Caracas' },
    { value: 'Maracaibo', label: 'Maracaibo' },
    { value: 'Barquisimeto', label: 'Barquisimeto' },
  ];

  const regionesOptions = [
    { value: 'Los Andes', label: 'Los Andes' },
    { value: 'Capital', label: 'Capital' },
    { value: 'Centro', label: 'Centro' },
    { value: 'Centroccidente', label: 'Centroccidente' },
    { value: 'Oriente', label: 'Oriente' },
    { value: 'Occidente', label: 'Occidente' },
  ];

  const regionesVentasOptions = [
    { value: 'ANDES (TACHIRA MERIDA TRUJILLO)', label: 'ANDES (TACHIRA MERIDA TRUJILLO)' },
    { value: 'CAPITAL (MIRANDA VARGAS)', label: 'CAPITAL (MIRANDA VARGAS)' },
    { value: 'CENTRO (GUARICO ARAGUA CARABOBO COJEDES)', label: 'CENTRO (GUARICO ARAGUA CARABOBO COJEDES)' },
    { value: 'CENTRO OCCIDENTE (LARA BARINAS APURE PORTUGUESA YARACUY)', label: 'CENTRO OCCIDENTE (LARA BARINAS APURE PORTUGUESA YARACUY)' },
    { value: 'EMPRESA', label: 'EMPRESA' },
    { value: 'INSULAR (NUEVA ESPARTA)', label: 'INSULAR (NUEVA ESPARTA)' },
    { value: 'OCCIDENTE (FALCON ZULIA)', label: 'OCCIDENTE (FALCON ZULIA)' },
    { value: 'ORIENTE NORTE (SUCRE ANZOATEGUI)', label: 'ORIENTE NORTE (SUCRE ANZOATEGUI)' },
    { value: 'ORIENTE SUR (BOLIVAR MONAGAS DELTA A)', label: 'ORIENTE SUR (BOLIVAR MONAGAS DELTA A)' },
  ];

  const statusesOptions = [
    { value: 'Vacante', label: 'Vacante' },
    { value: 'Cubierta', label: 'Cubierta' },
  ];

  const motivosOptions = [
    { value: 'Sustitución', label: 'Sustitución' },
    { value: 'Incremento de plantilla', label: 'Incremento de plantilla' },
  ];

  const tiposVacanteOptions = [
    { value: 'A', label: 'A' },
    { value: 'B', label: 'B' },
    { value: 'C', label: 'C' },
    { value: 'D', label: 'D' },
    { value: 'E', label: 'E' },
  ];

  const cargosOptions = cargosFiltrados.map(cargo => ({
    value: cargo.id,
    label: `${cargo.cargo} (Rol ${cargo.rol})`,
  }));

  const loadInitialData = useCallback(async () => {
    setLoadingData(true);
    try {
      const response = await getCargosDisponibles(profile?.id);
      
      if (response?.cargos && Array.isArray(response.cargos)) {
        setCargosFiltrados(response.cargos);
      } else if (Array.isArray(response)) {
        setCargosFiltrados(response);
      } else {
        setCargosFiltrados([]);
      }
      
      if (profile?.id) {
        const liderResponse = await getLiderDirecto(profile.id);
        
        if (liderResponse?.lider) {
          setLiderDirecto(liderResponse.lider);
        }
        
        if (liderResponse?.auto_aprobacion) {
          setAutoAprobacion(true);
        }
      }
    } catch (error) {
      console.error('Error cargando datos del formulario:', error);
      setCargosFiltrados([]);
    } finally {
      setLoadingData(false);
    }
  }, [getCargosDisponibles, getLiderDirecto, profile]);

  // Cargar datos iniciales
  useEffect(() => {
    if (isOpen) {
      loadInitialData();
      
      if (isEditing && editData) {
        setFormData({
          tipo_solicitud: editData.tipo_solicitud || 'Existente',
          cargo: editData.cargo || '',
          cargo_id: editData.cargo_id || null,
          departamento: editData.departamento || '',
          ubicacion: editData.ubicacion || '',
          region: editData.region || '',
          region_ventas: editData.region_ventas || '',
          status: editData.status || 'Vacante',
          motivo: editData.motivo || 'Sustitución',
          tipo_vacante: editData.tipo_vacante || 'A',
          solicitante_id: editData.solicitante_id || profile?.id,
          solicitante_nombre: editData.solicitante_nombre || profile?.nombre,
          solicitante_cargo: editData.solicitante_cargo || profile?.cargo,
          solicitante_rol: editData.solicitante_rol || profile?.rol,
          solicitante_area: editData.solicitante_area || profile?.area,
          solicitante_subarea1: editData.solicitante_subarea1 || profile?.subarea1,
          solicitante_subarea2: editData.solicitante_subarea2 || profile?.subarea2,
        });
      } else {
        setFormData({
          ...solicitudInicial,
          solicitante_id: profile?.id,
          solicitante_nombre: profile?.nombre,
          solicitante_cargo: profile?.cargo,
          solicitante_rol: profile?.rol,
          solicitante_area: profile?.area,
          solicitante_subarea1: profile?.subarea1,
          solicitante_subarea2: profile?.subarea2,
        });
      }
    }
  }, [isOpen, isEditing, editData, profile, loadInitialData]);

  const handleTipoSolicitudChange = (tipo) => {
    setFormData(prev => ({
      ...prev,
      tipo_solicitud: tipo,
      cargo: '',
      cargo_id: null,
      departamento: '',
      region_ventas: '',
    }));
  };

  const handleCargoExistenteChange = (cargoId) => {
    const cargoSeleccionado = cargosFiltrados.find(c => c.id === parseInt(cargoId));
    
    if (cargoSeleccionado) {
      setFormData(prev => ({
        ...prev,
        cargo_id: cargoSeleccionado.id,
        cargo: cargoSeleccionado.cargo,
        departamento: cargoSeleccionado.area || '',
        region_ventas: cargoSeleccionado.subarea1 || '',
      }));
    }
  };

  // Manejar cambio de región para autocompletar región de ventas
  const handleRegionChange = (value) => {
    setFormData(prev => ({
      ...prev,
      region: value,
      region_ventas: regionVentasMap[value] || prev.region_ventas || '',
    }));
    
    if (formErrors.region) {
      setFormErrors(prev => ({
        ...prev,
        region: null
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.cargo?.trim()) {
      errors.cargo = 'El cargo es requerido';
    }
    if (!formData.departamento?.trim()) {
      errors.departamento = 'El departamento es requerido';
    }
    if (!formData.ubicacion?.trim()) {
      errors.ubicacion = 'La ubicación es requerida';
    }
    if (!formData.region?.trim()) {
      errors.region = 'La región es requerida';
    }
    if (!formData.tipo_vacante?.trim()) {
      errors.tipo_vacante = 'El tipo de vacante es requerido';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        lider_id: liderDirecto?.id || null,
        lider_nombre: liderDirecto?.nombre || null,
        lider_cargo: liderDirecto?.cargo || null,
        lider_email: liderDirecto?.email || null,
        estado_aprobacion: 'Pendiente',
      };
      
      await onSubmit(dataToSend);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="cap-modal-overlay"
        onClick={onClose}
      >
        <motion.div 
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          className="cap-modal cap-modal-form"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="cap-modal-header">
            <div className="cap-modal-title">
              {isEditing ? 
                <FileCheck2 size={24} style={{ color: '#00ba52' }} /> : 
                <UserPlus size={24} style={{ color: '#00ba52' }} />
              }
              <div>
                <h3>{isEditing ? 'Editar Solicitud de Vacante' : 'Nueva Solicitud de Vacante'}</h3>
                <p>{isEditing ? `Editando: ${formData.cargo}` : 'Complete el formulario de solicitud'}</p>
              </div>
            </div>
            <button 
              className="cap-modal-close"
              onClick={onClose}
            >
              <X size={20} />
            </button>
          </div>
          
          {/* Body */}
          <div className="cap-modal-body">
            {loadingData ? (
              <div className="cap-loading">
                <Loader2 size={32} className="cap-spinner" />
                <span>Cargando datos...</span>
              </div>
            ) : (
              <div className="cap-form-section">
                <h4 className="cap-form-section-title">Formulario de Solicitud de Vacante</h4>

                {/* Información del Solicitante */}
                <div className="cap-solicitante-info">
                  <h5 className="cap-form-subsection-title">
                    <UserCheck size={16} />
                    Información del Solicitante
                  </h5>
                  <div className="cap-form-grid">
                    <div className="cap-form-group">
                      <label className="cap-form-label">Nombre</label>
                      <input type="text" value={formData.solicitante_nombre || ''} className="cap-form-input" disabled />
                    </div>
                    <div className="cap-form-group">
                      <label className="cap-form-label">Cargo</label>
                      <input type="text" value={formData.solicitante_cargo || ''} className="cap-form-input" disabled />
                    </div>
                    <div className="cap-form-group">
                      <label className="cap-form-label">Rol</label>
                      <input type="text" value={formData.solicitante_rol || ''} className="cap-form-input" disabled />
                    </div>
                    <div className="cap-form-group">
                      <label className="cap-form-label">Área</label>
                      <input type="text" value={formData.solicitante_area || ''} className="cap-form-input" disabled />
                    </div>
                  </div>
                </div>
                
                {/* Tipo de Solicitud */}
                <div className="cap-solicitante-info" style={{ paddingTop: '1rem', marginTop: '1rem' }}>
                  <h5 className="cap-form-subsection-title">
                    <BriefcaseBusiness size={16} />
                    Tipo de Solicitud
                  </h5>
                  <div className="cap-tipo-solicitud-toggle">
                    <button
                      type="button"
                      className={`cap-toggle-btn ${formData.tipo_solicitud === 'Existente' ? 'active' : ''}`}
                      onClick={() => handleTipoSolicitudChange('Existente')}
                    >
                      <UserCheck size={18} />
                      Puesto Existente
                    </button>
                    <button
                      type="button"
                      className={`cap-toggle-btn ${formData.tipo_solicitud === 'Nuevo' ? 'active' : ''}`}
                      onClick={() => handleTipoSolicitudChange('Nuevo')}
                    >
                      <UserPlus size={18} />
                      Nuevo Puesto
                    </button>
                  </div>
                  
                  <div className="cap-form-grid">
                    {/* Cargo */}
                    <div className="cap-form-group">
                      <label className="cap-form-label">Cargo *</label>
                      {formData.tipo_solicitud === 'Existente' ? (
                        <SearchableSelect
                          options={cargosOptions}
                          value={formData.cargo_id}
                          onChange={handleCargoExistenteChange}
                          placeholder="Buscar cargo existente..."
                          error={!!formErrors.cargo}
                          maxHeight={200}
                        />
                      ) : (
                        <input
                          type="text"
                          name="cargo"
                          value={formData.cargo}
                          onChange={handleInputChange}
                          className={`cap-form-input ${formErrors.cargo ? 'error' : ''}`}
                          placeholder="Nombre del nuevo cargo"
                        />
                      )}
                      {formErrors.cargo && <span className="cap-form-error">{formErrors.cargo}</span>}
                    </div>
                    
                    {/* Departamento */}
                    <div className="cap-form-group">
                      <label className="cap-form-label">Departamento *</label>
                      <input
                        type="text"
                        name="departamento"
                        value={formData.departamento}
                        onChange={handleInputChange}
                        className={`cap-form-input ${formErrors.departamento ? 'error' : ''}`}
                        placeholder="Ej. Trade Marketing"
                        disabled={formData.tipo_solicitud === 'Existente' && formData.cargo_id}
                      />
                      {formErrors.departamento && <span className="cap-form-error">{formErrors.departamento}</span>}
                    </div>
                    
                    {/* Ubicación */}
                    <div className="cap-form-group">
                      <label className="cap-form-label">Ubicación *</label>
                      <SearchableSelect
                        options={ubicacionesOptions}
                        value={formData.ubicacion}
                        onChange={(value) => handleSelectChange('ubicacion', value)}
                        placeholder="Buscar ubicación..."
                        error={!!formErrors.ubicacion}
                        maxHeight={180}
                      />
                      {formErrors.ubicacion && <span className="cap-form-error">{formErrors.ubicacion}</span>}
                    </div>
                    
                    {/* Región */}
                    <div className="cap-form-group">
                      <label className="cap-form-label">Región *</label>
                      <SearchableSelect
                        options={regionesOptions}
                        value={formData.region}
                        onChange={handleRegionChange}
                        placeholder="Buscar región..."
                        error={!!formErrors.region}
                        maxHeight={180}
                      />
                      {formErrors.region && <span className="cap-form-error">{formErrors.region}</span>}
                    </div>
                    
                    {/* Región de Ventas - Autocompletada */}
                    <div className="cap-form-group">
                      <label className="cap-form-label">Región de Ventas</label>
                      <SearchableSelect
                        options={regionesVentasOptions}
                        value={formData.region_ventas}
                        onChange={(value) => handleSelectChange('region_ventas', value)}
                        placeholder="Seleccionar región de ventas..."
                        disabled={formData.tipo_solicitud === 'Existente' && formData.cargo_id}
                        maxHeight={200}
                      />
                    </div>
                    
                    {/* Status */}
                    <div className="cap-form-group">
                      <label className="cap-form-label">Status *</label>
                      <SearchableSelect
                        options={statusesOptions}
                        value={formData.status}
                        onChange={(value) => handleSelectChange('status', value)}
                        placeholder="Seleccionar status..."
                        maxHeight={150}
                      />
                    </div>
                    
                    {/* Motivo */}
                    <div className="cap-form-group">
                      <label className="cap-form-label">Motivo *</label>
                      <SearchableSelect
                        options={motivosOptions}
                        value={formData.motivo}
                        onChange={(value) => handleSelectChange('motivo', value)}
                        placeholder="Seleccionar motivo..."
                        maxHeight={150}
                      />
                    </div>
                    
                    {/* Tipo de Vacante */}
                    <div className="cap-form-group">
                      <label className="cap-form-label">Tipo de Vacante *</label>
                      <SearchableSelect
                        options={tiposVacanteOptions}
                        value={formData.tipo_vacante}
                        onChange={(value) => handleSelectChange('tipo_vacante', value)}
                        placeholder="Seleccionar tipo..."
                        error={!!formErrors.tipo_vacante}
                        maxHeight={150}
                      />
                      {formErrors.tipo_vacante && <span className="cap-form-error">{formErrors.tipo_vacante}</span>}
                    </div>
                  </div>
                </div>
                
                {/* Líder Directo o Auto-Aprobación */}
                {autoAprobacion ? (
                  <div className="cap-lider-info">
                    <h5 className="cap-form-subsection-title">
                      <BadgeCheck size={16} />
                      Aprobación Automática
                    </h5>
                    <p className="cap-auto-aprobacion">
                      Como Coordinador de Inteligencia de Negocio, esta solicitud se aprobará automáticamente.
                    </p>
                  </div>
                ) : liderDirecto ? (
                  <div className="cap-lider-info">
                    <h5 className="cap-form-subsection-title">
                      <BadgeCheck size={16} />
                      Aprobador (Líder Directo)
                    </h5>
                    <div className="cap-form-grid">
                      <div className="cap-form-group">
                        <label className="cap-form-label">Nombre</label>
                        <input type="text" value={liderDirecto.nombre || ''} className="cap-form-input" disabled />
                      </div>
                      <div className="cap-form-group">
                        <label className="cap-form-label">Cargo</label>
                        <input type="text" value={liderDirecto.cargo || ''} className="cap-form-input" disabled />
                      </div>
                      {liderDirecto.email && (
                        <div className="cap-form-group">
                          <label className="cap-form-label">Email</label>
                          <input type="text" value={liderDirecto.email || ''} className="cap-form-input" disabled />
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="cap-modal-footer">
            <button className="cap-modal-btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button 
              className="cap-modal-btn-primary"
              onClick={handleSubmit}
              disabled={saving || loadingData}
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="cap-spinner" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={16} />
                  {isEditing ? 'Actualizar' : 'Enviar Solicitud'}
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}